// frontend/src/features/livestock/ShareCompareModal.tsx
// Share two tank shots, or two photos of one organism, by a public link
// (design 8.6; livestock compare plan 7.5). First an optional title, the name
// switches (all off: "Show tank name" for a tank; "Show name" and, when the
// organism has one, "Show scientific name" for an organism) and a preview of
// what viewers see, then Create link. Once the link exists: Copy and, where
// the browser has one, the share sheet, each its own tap (iOS drops the user
// gesture across an await).

import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Group, Modal, Stack, Switch, Text, TextInput } from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconCopy,
  IconAlertTriangle,
  IconInfoCircle,
  IconShare,
} from '@tabler/icons-react';
import { ShareLinkApiError } from '@/api/shareLinks';
import { RateLimitError } from '@/api/rateLimit';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useCreateShareLink } from '@/hooks/useShareLinks';
import type { LivestockType } from '@/types/livestock';
import {
  SHARE_TITLE_MAX,
  type ShareLink,
  type ShareLinkCreate,
  type ShareLinkCreated,
  type ShareLinkKind,
} from '@/types/shareLinks';
import { gapLabel, type ComparePair } from './compareShots';
import { formatPhotoDay } from './photoInboxFormat';
import { organismComparisonTitle, TANK_COMPARISON_TITLE } from './shareHeadings';
import { SHARED_BY, canOpenShareSheet, copyLink, openShareSheet } from './shareLinkActions';

/** The organism whose photos are shared: the names a viewer can be shown, and its type. */
export interface ShareOrganism {
  commonName: string;
  scientificName: string | null;
  livestockType: LivestockType;
}

export interface ShareCompareModalProps {
  opened: boolean;
  onClose: () => void;
  tankId: string;
  /** The tank's name, for "Show tank name" (tank links only). */
  tankName: string;
  pair: ComparePair;
  /** The photos' dates, ISO 8601 */
  beforeDate: string;
  afterDate: string;
  /**
   * What to share (usePhotoSubject's shareTarget). Without it, the pair's two
   * tank shots (a tank_compare link).
   */
  target?: ShareLinkCreate;
  /** For an organism_compare target: its names and type. */
  organism?: ShareOrganism;
}

/** The switches as asked for, by kind. */
export type ShareSettings =
  | { kind: 'tank_compare'; showTankName: boolean }
  | {
      kind: 'organism_compare';
      /** null: the switch wasn't offered (the organism has no such name), so it isn't compared. */
      showName: boolean | null;
      showScientificName: boolean | null;
    };

/** What the pair's existing link shows that wasn't asked for: names a viewer will see. */
export function shownButNotAsked(link: ShareLink, asked: ShareSettings): string[] {
  if (link.kind === 'tank_compare' && asked.kind === 'tank_compare') {
    return link.show_tank_name && !asked.showTankName ? ['the tank name'] : [];
  }
  if (link.kind === 'organism_compare' && asked.kind === 'organism_compare') {
    const shown: string[] = [];
    if (link.show_subject_name && asked.showName === false) shown.push('the name');
    if (link.show_scientific_name && asked.showScientificName === false) {
      shown.push('the scientific name');
    }
    return shown;
  }
  return [];
}

export function createErrorMessage(error: Error, kind: ShareLinkKind = 'tank_compare'): string {
  if (error instanceof RateLimitError) return error.message;
  if (error instanceof ShareLinkApiError) {
    if (error.status === 409) {
      // The per-tank cap counts both kinds, so name both places links are stopped.
      return "This tank has as many shared links as it can have. Stop sharing some on Tank Shots or on an organism's page, then try again.";
    }
    if (error.status === 404) {
      return kind === 'organism_compare'
        ? 'One of these photos is no longer here. Pick another and try again.'
        : 'One of these shots is no longer in this tank. Pick another and try again.';
    }
    if (error.status === 422) return 'Check the title: one line of plain text.';
  }
  return "The link wasn't created. Try again in a moment.";
}

/**
 * One line of plain text, as the server takes it: line breaks and tabs become
 * spaces, other control characters and bidi overrides go.
 */
export function cleanTitle(value: string): string {
  return (
    value
      .replace(/[\r\n\t]+/g, ' ')
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/g, '')
  );
}

/**
 * The pair's live link came back (200): it keeps its own title and name
 * switches, so say where they differ from what was asked for.
 */
export function existingNote(link: ShareLink, title: string | null, asked: ShareSettings): string {
  const parts = [
    link.kind === 'organism_compare'
      ? 'These two photos are already shared, so this is the same link.'
      : 'These two shots are already shared, so this is the same link.',
  ];
  if (link.title !== title) {
    parts.push(link.title ? `Its title stays "${link.title}".` : 'It stays without a title.');
  }
  if (link.kind === 'tank_compare' && asked.kind === 'tank_compare') {
    if (link.show_tank_name !== asked.showTankName) {
      parts.push(
        link.show_tank_name ? 'It shows the tank name.' : "It doesn't show the tank name."
      );
    }
  } else if (link.kind === 'organism_compare' && asked.kind === 'organism_compare') {
    // Only the switches the modal offered: a name the organism no longer has isn't mentioned
    if (asked.showName !== null && link.show_subject_name !== asked.showName) {
      parts.push(link.show_subject_name ? 'It shows the name.' : "It doesn't show the name.");
    }
    if (
      asked.showScientificName !== null &&
      link.show_scientific_name !== asked.showScientificName
    ) {
      parts.push(
        link.show_scientific_name
          ? 'It shows the scientific name.'
          : "It doesn't show the scientific name."
      );
    }
  }
  // It shows more than was asked: say how to share without it
  const extra = shownButNotAsked(link, asked);
  if (extra.length > 0) {
    const where = link.kind === 'organism_compare' ? "on this organism's page" : 'on Tank Shots';
    parts.push(
      `To share without ${extra.length === 1 ? 'it' : 'them'}, stop sharing this link under Shared links ${where}, then create a new one.`
    );
  }
  return parts.join(' ');
}

type CopyState = 'idle' | 'copied' | 'failed';

export function ShareCompareModal({
  opened,
  onClose,
  tankId,
  tankName,
  pair,
  beforeDate,
  afterDate,
  target,
  organism,
}: ShareCompareModalProps): JSX.Element {
  const create = useCreateShareLink(tankId);
  const isMobile = useIsMobile();
  /** 44 px tap targets on a phone. */
  const tap = isMobile ? 44 : undefined;
  const copyRef = useRef<HTMLButtonElement>(null);
  const [title, setTitle] = useState('');
  const [showTankName, setShowTankName] = useState(false);
  const [showName, setShowName] = useState(false);
  const [showScientificName, setShowScientificName] = useState(false);
  const [result, setResult] = useState<ShareLinkCreated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const [shareFailed, setShareFailed] = useState(false);

  // Each opening starts again; so does another pair.
  const pairKey = `${pair.beforeId}-${pair.afterId}`;
  const [openedFor, setOpenedFor] = useState<string | null>(opened ? pairKey : null);
  const current = opened ? pairKey : null;
  if (current !== openedFor) {
    setOpenedFor(current);
    if (current) {
      setTitle('');
      setShowTankName(false);
      setShowName(false);
      setShowScientificName(false);
      setResult(null);
      setError(null);
      setCopyState('idle');
      setShareFailed(false);
    }
  }

  const days = `${formatPhotoDay(beforeDate)} to ${formatPhotoDay(afterDate)}`;
  const gap = gapLabel(beforeDate, afterDate);
  const trimmed = title.trim();
  const kind: ShareLinkKind = target?.kind ?? 'tank_compare';
  const isOrganism = target?.kind === 'organism_compare';
  const commonName = organism?.commonName.trim() || null;
  const scientificName = organism?.scientificName?.trim() || null;
  const settings: ShareSettings = isOrganism
    ? {
        kind: 'organism_compare',
        showName: commonName ? showName : null,
        showScientificName: scientificName ? showScientificName : null,
      }
    : { kind: 'tank_compare', showTankName };
  const fallbackTitle = isOrganism
    ? organismComparisonTitle(organism?.livestockType)
    : TANK_COMPARISON_TITLE;

  /** The body for the kind: only that kind's switches (the server refuses the others). */
  const body = (): ShareLinkCreate =>
    target?.kind === 'organism_compare'
      ? {
          kind: 'organism_compare',
          target: target.target,
          title: trimmed || null,
          show_subject_name: !!commonName && showName,
          show_scientific_name: !!scientificName && showScientificName,
        }
      : {
          kind: 'tank_compare',
          target: target?.target ?? { before_id: pair.beforeId, after_id: pair.afterId },
          title: trimmed || null,
          show_tank_name: showTankName,
        };

  const submit = (): void => {
    setError(null);
    create.mutate(body(), {
      onSuccess: (created) => setResult(created),
      onError: (failure) => setError(createErrorMessage(failure, kind)),
    });
  };

  const copy = async (url: string): Promise<void> => {
    setCopyState((await copyLink(url)) ? 'copied' : 'failed');
  };

  const share = async (shareTitle: string, url: string): Promise<void> => {
    setShareFailed(false);
    const outcome = await openShareSheet(shareTitle, url);
    if (outcome !== 'failed') return;
    // No share sheet after all: copy instead (if the browser still allows it).
    setShareFailed(true);
    await copy(url);
  };

  const link = result?.link ?? null;

  // The form (and the focused Create link) is gone: focus moves to Copy link.
  useEffect(() => {
    if (link) copyRef.current?.focus();
  }, [link]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={link ? 'Link ready' : 'Share this comparison'}
      size="md"
      centered
      closeButtonProps={{ size: tap ?? 'md', 'aria-label': 'Close' }}
    >
      {!link ? (
        <Stack gap="md">
          <TextInput
            label="Title"
            description="Optional. Viewers see it as plain text."
            placeholder="e.g. Seven months of growth"
            value={title}
            maxLength={SHARE_TITLE_MAX}
            size={isMobile ? 'md' : 'sm'}
            styles={{ input: { minHeight: tap } }}
            onChange={(event) => setTitle(cleanTitle(event.currentTarget.value))}
            rightSection={
              title.length > SHARE_TITLE_MAX - 20 ? (
                <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {SHARE_TITLE_MAX - title.length}
                </Text>
              ) : null
            }
          />
          {isOrganism ? (
            <>
              {commonName && (
                <Switch
                  label="Show name"
                  description={`Viewers see "${commonName}"`}
                  checked={showName}
                  onChange={(event) => setShowName(event.currentTarget.checked)}
                />
              )}
              {scientificName && (
                <Switch
                  label="Show scientific name"
                  description={`Viewers see "${scientificName}"`}
                  checked={showScientificName}
                  onChange={(event) => setShowScientificName(event.currentTarget.checked)}
                />
              )}
            </>
          ) : (
            <Switch
              label="Show tank name"
              description={`Viewers see "${tankName}"`}
              checked={showTankName}
              onChange={(event) => setShowTankName(event.currentTarget.checked)}
            />
          )}

          <Card withBorder radius="md" p="sm" data-testid="share-preview">
            <Stack gap={4}>
              <Text size="xs" fw={500} c="dimmed" tt="uppercase">
                What viewers see
              </Text>
              <Text size="xs" c="dimmed">
                {SHARED_BY}
              </Text>
              {trimmed && (
                <Text size="sm" fw={600} style={{ overflowWrap: 'anywhere' }}>
                  {trimmed}
                </Text>
              )}
              {!isOrganism && showTankName && <Text size="sm">{tankName}</Text>}
              {isOrganism && showName && commonName && (
                <Text size="sm" fw={500} style={{ overflowWrap: 'anywhere' }}>
                  {commonName}
                </Text>
              )}
              {isOrganism && showScientificName && scientificName && (
                <Text size="sm" fs="italic" style={{ overflowWrap: 'anywhere' }}>
                  {scientificName}
                </Text>
              )}
              <Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {gap} · {days}
              </Text>
              <Text size="xs" c="dimmed">
                And these two photos. No notes, times or other details.
              </Text>
            </Stack>
          </Card>

          <Text size="xs" c="dimmed">
            Anyone with the link can see it until you stop sharing. Previews that messengers and
            forums have already made can&apos;t be recalled.
          </Text>

          {error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert">
              {error}
            </Alert>
          )}

          <Group justify="flex-end" gap="xs">
            <Button
              variant="subtle"
              color="gray"
              h={tap}
              onClick={onClose}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button h={tap} onClick={submit} loading={create.isPending}>
              Create link
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="md">
          {!result?.created &&
            (shownButNotAsked(link, settings).length > 0 ? (
              // It shows a name that wasn't asked for: a warning, read out at once
              <Alert
                color="yellow"
                variant="light"
                icon={<IconAlertTriangle size={16} />}
                role="alert"
                data-testid="share-existing-warning"
              >
                {existingNote(link, trimmed || null, settings)}
              </Alert>
            ) : (
              <Alert color="teal" variant="light" icon={<IconInfoCircle size={16} />}>
                {existingNote(link, trimmed || null, settings)}
              </Alert>
            ))}
          <TextInput
            label="Link"
            value={link.url}
            readOnly
            size={isMobile ? 'md' : 'sm'}
            onFocus={(event) => event.currentTarget.select()}
            styles={{ input: { fontVariantNumeric: 'tabular-nums', minHeight: tap } }}
          />
          <Group gap="xs" grow>
            <Button
              ref={copyRef}
              h={tap}
              variant={canOpenShareSheet() ? 'light' : 'filled'}
              leftSection={
                copyState === 'copied' ? <IconCheck size={16} /> : <IconCopy size={16} />
              }
              onClick={() => void copy(link.url)}
            >
              {copyState === 'copied' ? 'Copied' : 'Copy link'}
            </Button>
            {canOpenShareSheet() && (
              <Button
                h={tap}
                leftSection={<IconShare size={16} />}
                onClick={() => void share(link.title ?? fallbackTitle, link.url)}
              >
                Share
              </Button>
            )}
          </Group>
          <Text size="sm" role="status">
            {copyState === 'failed'
              ? "Couldn't copy: select the link above and copy it."
              : shareFailed && copyState === 'copied'
                ? "Sharing isn't available here, so the link was copied."
                : ''}
          </Text>
          <Text size="xs" c="dimmed">
            {isOrganism
              ? "Stop sharing any time under Shared links on this organism's page."
              : 'Stop sharing any time under Shared links on Tank Shots.'}{' '}
            Previews that messengers and forums have already made can&apos;t be recalled.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" color="gray" h={tap} onClick={onClose}>
              Done
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
